import {
  PaginationState,
  SortingState,
  ColumnDef,
  OnChangeFn,
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  Header,
  Row
} from '@tanstack/react-table';
import { useState, useEffect, useMemo, useRef } from 'react';
import { safeParseInt } from '../../../common/utils';
import {
  PaginationConfig,
  IExpandableConfig,
  DefaultDataType,
  IUseTableStateProps,
  TColumn,
  SortingConfig
} from './types';
import _ from 'lodash';

const DEFAULT_PAGINATION_CONFIG: PaginationConfig = Object.freeze({
  default: {
    pageIndex: 0,
    pageSize: 20
  },
  pageSizeOptions: [10, 20, 50, 100],
  showSizeChanger: true,
  lsKey: null,
  hideIfAllDataIsShown: false
});

const DEFAULT_EXPANDABLE_CONFIG: Required<IExpandableConfig<any>> = Object.freeze({
  isRowExpandable: true,
  getChildren: () => null,
  isEnabled: false,
  defaultExpandedRowKeys: [],
  expandedRowKeys: undefined as any,
  icon: <i className="fa-solid fa-chevron-right" />,
  showToggleColumn: true,
  allowMultipleExpandedRows: true,
  onSetExpansionType: undefined as any,
  onToggleExpand: undefined as any
});

const DEFAULT_SORTING_CONFIG: SortingConfig = Object.freeze({
  enableMultiSort: false,
  manualSorting: false,
  maxMultiSortColCount: undefined,
  isMultiSortEvent: () => true, // by default, allow multi-sorting on mouse click (not on shift+click),
  onSortingChange: undefined
});

const getLimitFromLocalStorage = (lsKey: string | null): number | undefined => {
  if (!lsKey) {
    return undefined;
  }

  const limit = localStorage.getItem(lsKey);
  return limit ? safeParseInt(limit) : undefined;
};

export const tableLib = {
  DEFAULT_PAGINATION_CONFIG,
  DEFAULT_EXPANDABLE_CONFIG,
  getLimitFromLocalStorage
};

export const useTableState = <T extends DefaultDataType = DefaultDataType>(props: IUseTableStateProps<T>) => {
  const {
    columns,
    data,
    sorting: propSorting,
    pagination: propPagination,
    expandable,
    rowSelection,
    getRowKey
  } = props;

  const paginationConfig = Object.assign(
    {},
    DEFAULT_PAGINATION_CONFIG,
    typeof propPagination === 'object' ? propPagination : {}
  );

  if (paginationConfig.manualPagination && paginationConfig.showSizeChanger && !paginationConfig.onPageSizeChange) {
    paginationConfig.showSizeChanger = false;
  }

  const expandableConfig = Object.assign(
    {},
    DEFAULT_EXPANDABLE_CONFIG as IExpandableConfig<T>,
    // if expandable is defined, enable it
    expandable ? { isEnabled: true } : {},
    expandable
  );

  const sortingConfig = Object.assign({}, DEFAULT_SORTING_CONFIG, typeof propSorting === 'object' ? propSorting : {});

  const isExpandedRowKeysControlled = typeof expandableConfig?.expandedRowKeys !== 'undefined';
  const [expandedRowKeysState, setExpandedRowKeysState] = useState<string[]>(
    expandableConfig?.expandedRowKeys || expandableConfig?.defaultExpandedRowKeys || []
  );
  const expandedRowKeys = isExpandedRowKeysControlled ? expandableConfig.expandedRowKeys || [] : expandedRowKeysState;

  // Track if we've already applied the default expansion from props (e.g., from URL hash)
  // This ensures we only apply once, so user can collapse without it re-expanding
  const hasAppliedDefaultExpansionRef = useRef(false);

  // Sync expandedRowKeys when defaultExpandedRowKeys changes (e.g., from URL hash parsing after data loads)
  useEffect(() => {
    const defaultKeys = expandableConfig?.defaultExpandedRowKeys || [];
    if (!hasAppliedDefaultExpansionRef.current && defaultKeys.length > 0) {
      setExpandedRowKeysState(defaultKeys);
      hasAppliedDefaultExpansionRef.current = true;
    }
  }, [expandableConfig?.defaultExpandedRowKeys]);

  useEffect(() => {
    if (!isExpandedRowKeysControlled) {
      return;
    }

    setExpandedRowKeysState(expandableConfig?.expandedRowKeys || []);
  }, [expandableConfig?.expandedRowKeys, isExpandedRowKeysControlled]);

  const enablePagination = !!propPagination;

  const [paginationState, setPaginationState] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: paginationConfig.default?.pageSize ?? 20
  });

  useEffect(() => {
    if (paginationConfig.lsKey) {
      const defaultLimit = getLimitFromLocalStorage(paginationConfig.lsKey);
      if (defaultLimit) {
        setPaginationState((prev) => ({ ...prev, pageSize: defaultLimit }));
      }
    }
  }, []);

  const [sortingState, setSortingState] = useState<SortingState>(sortingConfig?.default || []);

  const tableColumns = useMemo(
    () =>
      columns.flatMap((c) => {
        const result = { ...c } as ColumnDef<T> & { accessorKey?: string; accessorFn?: (row: T) => any };

        if (c.sortingFn) {
          result.sortingFn = (aRow, bRow, columnId) => {
            const aOriginal = aRow.original as T;
            const bOriginal = bRow.original as T;

            let accessorA: any;
            let accessorB: any;

            if (typeof c.accessor === 'function') {
              accessorA = c.accessor(aOriginal);
              accessorB = c.accessor(bOriginal);
            } else if (c.accessor) {
              accessorA = _.get(aOriginal, c.accessor);
              accessorB = _.get(bOriginal, c.accessor);
            } else {
              accessorA = undefined;
              accessorB = undefined;
            }

            return c.sortingFn!(aOriginal, bOriginal, columnId, accessorA, accessorB);
          };
        }

        if (c.sortable !== false && sortingConfig.allTableSortable) {
          result.enableSorting = true;
        } else {
          result.enableSorting = c.sortable || false;
        }

        if (typeof c.accessor === 'string') {
          if (!c.id) {
            result.id = c.accessor;
          }

          result.accessorKey = c.accessor;
        }

        if (typeof c.accessor === 'function') {
          result.accessorFn = c.accessor;
        }

        result.sortDescFirst = false;

        if (c.hidden) {
          return [];
        }

        return [result];
      }) as ColumnDef<T>[],
    [columns, sortingConfig.allTableSortable]
  );

  const columnsWithDefaultSortOrder = useMemo(
    () =>
      columns
        .filter((c) => c.defaultSortOrder)
        .map((c) => c.id || (typeof c.accessor === 'function' ? c.accessor(data[0]) : c.accessor))
        .sort(),
    [columns]
  );

  useEffect(() => {
    if (propSorting?.sorting) {
      const isSortingEqual = _.isEqual(propSorting.sorting, sortingState);

      if (isSortingEqual) {
        return;
      }

      setSortingState(propSorting.sorting);
    } else {
      const columnsWithDefaultSorting = columns
        .filter((c) => c.defaultSortOrder)
        .map((c) => {
          const accessor = typeof c.accessor === 'function' ? c.accessor(data[0]) : c.accessor;

          const id = c.id || (accessor ? accessor.toString() : '');

          return {
            id,
            desc: c.defaultSortOrder === 'desc'
          };
        });

      if (columnsWithDefaultSorting.length > 0) {
        setSortingState(columnsWithDefaultSorting);
      }
    }
  }, [propSorting, columnsWithDefaultSortOrder]);

  useEffect(() => {
    const paginationToUpdate = {
      pageIndex: paginationConfig.pageIndex ?? paginationState.pageIndex,
      pageSize: paginationConfig.pageSize ?? paginationState.pageSize
    };

    if (!_.isEqual(paginationToUpdate, paginationState)) {
      setPaginationState(paginationToUpdate);
    }
  }, [paginationConfig.pageIndex, paginationConfig.pageSize, paginationState]);

  const handleSortingChange: OnChangeFn<SortingState> = (updaterOrValue) => {
    const newSortingValue = updaterOrValue instanceof Function ? updaterOrValue(sortingState) : updaterOrValue;
    if (sortingConfig?.onSortingChange) {
      sortingConfig.onSortingChange(newSortingValue);
    } else {
      setSortingState(newSortingValue);
    }
  };

  const handlePaginationChange: OnChangeFn<PaginationState> = (updaterOrValue) => {
    const newPaginationValue = updaterOrValue instanceof Function ? updaterOrValue(paginationState) : updaterOrValue;

    const pageSizeChanged = newPaginationValue.pageSize !== paginationState.pageSize;
    const pageIndexChanged = newPaginationValue.pageIndex !== paginationState.pageIndex;

    if (pageSizeChanged && paginationConfig?.onPageSizeChange) {
      paginationConfig.onPageSizeChange(newPaginationValue.pageSize);
    } else if (pageIndexChanged && paginationConfig?.onPageChange) {
      paginationConfig.onPageChange(newPaginationValue.pageIndex);
    } else {
      setPaginationState(newPaginationValue);
    }
  };

  const pageCount =
    paginationConfig.pageCount ??
    Math.ceil(
      (typeof paginationConfig.total === 'number' ? paginationConfig.total : data.length) / paginationState.pageSize
    );

  const tableInstance = useReactTable<T>({
    data,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    maxMultiSortColCount: sortingConfig?.maxMultiSortColCount,
    columns: tableColumns,
    pageCount: pageCount,
    enableMultiSort: sortingConfig?.enableMultiSort,
    enableSorting: true,
    manualSorting: sortingConfig?.manualSorting,
    manualPagination: paginationConfig?.manualPagination,
    state: {
      sorting: sortingState,
      pagination: enablePagination
        ? paginationState
        : {
            pageIndex: 0,
            pageSize: data.length
          }
    },
    onSortingChange: handleSortingChange,
    isMultiSortEvent: sortingConfig?.isMultiSortEvent,
    ...(enablePagination && {
      getPaginationRowModel: getPaginationRowModel(),
      onPaginationChange: handlePaginationChange
    })
  });

  const isEmpty = !data || data.length === 0;

  const pagination = {
    currentPage: paginationState.pageIndex + 1,
    isFunctionAction: true,
    prevEnabled: tableInstance.getCanPreviousPage(),
    nextEnabled: tableInstance.getCanNextPage(),
    firstEnabled: tableInstance.getCanPreviousPage(),
    lastEnabled: tableInstance.getCanNextPage(),
    toFirst: () => tableInstance.firstPage(),
    toPrev: () => tableInstance.previousPage(),
    toNext: () => tableInstance.nextPage(),
    toLast: () => tableInstance.lastPage(),
    changePageSize: (pageSize: number) => {
      tableInstance.setPageSize(pageSize);
      if (paginationConfig.lsKey) {
        localStorage.setItem(paginationConfig.lsKey, pageSize.toString());
      }
    },
    pageSizeOptions: paginationConfig.pageSizeOptions,
    showSizeChanger: paginationConfig.showSizeChanger,
    pageSize: paginationState.pageSize,
    pageCount: paginationConfig.pageCount ?? tableInstance.getPageCount()
  };

  const isPaginationVisible =
    enablePagination &&
    (paginationConfig.hideIfAllDataIsShown
      ? tableInstance.getCanNextPage() || tableInstance.getCanPreviousPage()
      : true);

  const columnsLength =
    columns.length + (expandableConfig.isEnabled && expandableConfig.showToggleColumn ? 1 : 0) + (rowSelection ? 1 : 0);

  const getHeaderSorting = (column: Header<T, unknown>) => {
    const original = column.getContext().column.columnDef as TColumn<T>;
    const sortDirections = original.sortDirections || 'both';

    const clickHandler = (event: React.MouseEvent<any>) => {
      const nextSortOrder = column.column.getNextSortingOrder();
      const original = column.getContext().column.columnDef as TColumn<T>;

      const isMultiSort =
        typeof original.enableMultiSort === 'boolean' ? original.enableMultiSort : sortingConfig?.enableMultiSort;

      if (isEmpty) {
        return;
      }

      if (sortDirections === 'both') {
        column.column.getToggleSortingHandler()?.(event);
        return;
      }

      if (nextSortOrder === false) {
        column.column.clearSorting();
        return;
      }

      if (nextSortOrder === 'asc' && !sortDirections.includes('asc')) {
        column.column.toggleSorting(true, isMultiSort);
        return;
      }

      if (nextSortOrder === 'desc' && !sortDirections.includes('desc')) {
        column.column.clearSorting();
        return;
      }

      column.column.toggleSorting(nextSortOrder === 'desc', isMultiSort);
    };

    return {
      clickHandler,
      sortDirections
    };
  };

  const getExpandableRow = (row: Row<T>) => {
    const isRowExpandable =
      expandableConfig?.isEnabled &&
      (typeof expandableConfig?.isRowExpandable === 'function'
        ? expandableConfig.isRowExpandable(row.original as T)
        : expandableConfig?.isRowExpandable);

    const handleClickExpand = () => {
      if (!getRowKey) {
        throw new Error('getRowKey is required for expandable rows');
      }

      const rowKey = getRowKey(row.original as T);
      const isCurrentlyExpanded = expandedRowKeys.includes(rowKey);

      if (!isExpandedRowKeysControlled) {
        if (isCurrentlyExpanded) {
          setExpandedRowKeysState((prev) => prev.filter((key) => key !== rowKey));
        } else {
          setExpandedRowKeysState((prev) =>
            expandableConfig.allowMultipleExpandedRows ? [...prev, rowKey] : [rowKey]
          );
        }
      }

      // Notify parent when row is toggled via direct click
      if (expandableConfig?.onToggleExpand) {
        expandableConfig.onToggleExpand(rowKey, !isCurrentlyExpanded);
      }
    };

    const isExpanded = getRowKey ? expandedRowKeys.includes(getRowKey(row.original as T)) : false;

    return {
      isRowExpandable,
      handleClickExpand,
      isExpanded
    };
  };

  return {
    tableInstance,
    isPaginationVisible,
    columnsLength,
    pagination,
    setPagination: setPaginationState,
    sorting: sortingState,
    sortingConfig,
    handleSortingChange,
    isEmpty,
    expandedRowKeys,
    setExpandedRowKeys: setExpandedRowKeysState,
    expandableConfig,
    expandable,
    getHeaderSorting,
    getRowKey,
    getExpandableRow,
    paginationConfig
  };
};
